import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Toggle from "@atlaskit/toggle";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { saveFlow, getFlows, fetchFlowRunnerStatus } from "../../../redux/actions/integrationActions";
import FlowActionMenu from "./FlowActionMenu";
import Button from "@atlaskit/button/new";
import Spinner from "@atlaskit/spinner";
import { Box, Inline } from "@atlaskit/primitives";
import { formatDate } from "../../../common/utils/formatter";
import Lozenge from "@atlaskit/lozenge";
import Tooltip from "@atlaskit/tooltip";
import EditIcon from "@atlaskit/icon/core/edit";
import PlusSquareIcon from "@atlaskit/icon/core/plus-square";
import MinusSquareIcon from "@atlaskit/icon/core/minus-square";
import ShareFlowLink from "../ShareFlowLink";
import { ConfigProvider, Popover, Table } from "antd";
import { getTriggerName } from "./formatTriggerType";
import { columns } from "./listHeaders";
import { getStatusAppearance, formatRunTimestamp } from "../status/flowStatusUtils";
import FlowGroupNameEditor from "./FlowGroupNameEditor";

const areSameArrayValues = (left, right) => {
  if (left === right) {
    return true;
  }
  if (!left && !right) {
    return true;
  }
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => Object.is(value, right[index]));
};

const areSameSortStates = (left, right) => {
  if (left === right) {
    return true;
  }
  if (!left && !right) {
    return true;
  }

  return Boolean(left && right && left.columnKey === right.columnKey && left.order === right.order);
};

const IntegrationListV2 = () => {
  const [tree, setTree] = useState([]);
  const flows = useSelector((state) => state.integration.flows);
  const flowsMetadata = useSelector((state) => state.integration.flowsMetadata);
  const flowRunnerStatuses = useSelector((state) => state.integration.flowRunnerStatuses || {});
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastLoadedFlowId, setLastLoadedFlowId] = useState(undefined);
  const [enabledFilter, setEnabledFilter] = useState(() => {
    if (typeof window === "undefined") {
      return null;
    }
    try {
      const storedValue = window.localStorage.getItem("integrationListEnabledFilter");
      const parsedValue = storedValue ? JSON.parse(storedValue) : null;
      return Array.isArray(parsedValue) && parsedValue.length ? parsedValue : null;
    } catch (error) {
      return null;
    }
  });
  const [triggerFilter, setTriggerFilter] = useState(null);
  const [sortState, setSortState] = useState(() => {
    if (typeof window === "undefined") {
      return null;
    }
    try {
      const storedValue = window.localStorage.getItem("integrationListModifiedSortOrder");
      return storedValue === "ascend" || storedValue === "descend" ? { columnKey: "modified", order: storedValue } : null;
    } catch (error) {
      return null;
    }
  });
  const [editingGroupName, setEditingGroupName] = useState(null);
  const [pendingGroupName, setPendingGroupName] = useState("");
  const dispatch = useDispatch();
  const url = "/integration";
  const requestedStatusFlowIds = useRef(new Set());
  const flowRunnerStatusesRef = useRef(flowRunnerStatuses);

  useEffect(() => {
    flowRunnerStatusesRef.current = flowRunnerStatuses;
  }, [flowRunnerStatuses]);

  useEffect(() => {
    if (flows) {
      const sortedFlows = [...flows].sort((a, b) => a.name.localeCompare(b.name));
      setTree(sortedFlows);
      setLastLoadedFlowId(flowsMetadata?.lastEvaluatedKey?.id || undefined);
      setIsLoadingMore(false);
    }
  }, [flows, flowsMetadata]);

  useEffect(() => {
    if (!Array.isArray(flows)) {
      return;
    }

    flows.forEach((flow) => {
      if (!flow?.id || requestedStatusFlowIds.current.has(flow.id)) {
        return;
      }

      const hasLoadedStatus = Object.prototype.hasOwnProperty.call(flowRunnerStatusesRef.current, flow.id);
      requestedStatusFlowIds.current.add(flow.id);
      if (!hasLoadedStatus) {
        dispatch(fetchFlowRunnerStatus(flow.id));
      }
    });
  }, [dispatch, flows]);

  const toggleEnabled = useCallback(
    (selectedFlow) => {
      selectedFlow.enabled = !selectedFlow.enabled;
      dispatch(saveFlow(selectedFlow, false));
    },
    [dispatch],
  );

  const loadMore = () => {
    setIsLoadingMore(true);
    dispatch(getFlows(flowsMetadata?.lastEvaluatedKey?.id));
  };

  const startEditGroupName = useCallback((groupName) => {
    setEditingGroupName(groupName);
    setPendingGroupName(groupName);
  }, []);

  const cancelEditGroupName = useCallback(() => {
    setEditingGroupName(null);
    setPendingGroupName("");
  }, []);

  const handleEditGroupNameChange = useCallback((groupName, open) => {
    if (open) {
      startEditGroupName(groupName);
      return;
    }

    cancelEditGroupName();
  }, [cancelEditGroupName, startEditGroupName]);

  const renameGroup = useCallback(() => {
    if (!editingGroupName) {
      return;
    }

    const nextGroupName = pendingGroupName.trim();
    tree
      .filter((flow) => (flow.groupName || "").trim() === editingGroupName)
      .forEach((flow) => {
        dispatch(
          saveFlow(
            {
              ...flow,
              groupName: nextGroupName,
            },
            false,
          ),
        );
      });

    setEditingGroupName(null);
    setPendingGroupName("");
  }, [dispatch, editingGroupName, pendingGroupName, tree]);

  const handleTableChange = useCallback((_, filters, sorter) => {
    const selectedFilter = filters?.enabled;
    const selectedTriggerFilter = filters?.trigger;
    const hasSelection = Array.isArray(selectedFilter) && selectedFilter.length > 0;
    const hasTriggerSelection = Array.isArray(selectedTriggerFilter) && selectedTriggerFilter.length > 0;
    const resolvedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextSortState =
      resolvedSorter?.columnKey && (resolvedSorter.order === "ascend" || resolvedSorter.order === "descend")
        ? { columnKey: resolvedSorter.columnKey, order: resolvedSorter.order }
        : null;
    const nextModifiedSortOrder =
      resolvedSorter?.columnKey === "modified" && (resolvedSorter.order === "ascend" || resolvedSorter.order === "descend")
        ? resolvedSorter.order
        : null;

    if (typeof window !== "undefined") {
      if (hasSelection) {
        window.localStorage.setItem("integrationListEnabledFilter", JSON.stringify(selectedFilter));
      } else {
        window.localStorage.removeItem("integrationListEnabledFilter");
      }

      if (nextModifiedSortOrder) {
        window.localStorage.setItem("integrationListModifiedSortOrder", nextModifiedSortOrder);
      } else {
        window.localStorage.removeItem("integrationListModifiedSortOrder");
      }
    }

    const nextEnabledFilter = hasSelection ? selectedFilter : null;
    const nextTriggerFilter = hasTriggerSelection ? selectedTriggerFilter : null;

    setEnabledFilter((previousFilter) => (areSameArrayValues(previousFilter, nextEnabledFilter) ? previousFilter : nextEnabledFilter));
    setTriggerFilter((previousFilter) => (areSameArrayValues(previousFilter, nextTriggerFilter) ? previousFilter : nextTriggerFilter));
    setSortState((previousSortState) => (areSameSortStates(previousSortState, nextSortState) ? previousSortState : nextSortState));
  }, []);

  const renderExpandIcon = useCallback(({ expanded, onExpand, record }) => {
    if (!record?.children?.length) {
      return null;
    }

    return (
      <button
        type="button"
        onClick={(event) => onExpand(record, event)}
        style={{
          border: 0,
          background: "transparent",
          padding: 0,
          marginRight: 8,
          cursor: "pointer",
          color: "var(--ds-text-subtle, #44546f)",
        }}
        aria-label={expanded ? "Collapse group" : "Expand group"}
      >
        {expanded ? <MinusSquareIcon label="" /> : <PlusSquareIcon label="" />}
      </button>
    );
  }, []);

  const flowRows = useMemo(
    () =>
      tree.map((flow) => {
        const statuses = flow?.id ? flowRunnerStatuses[flow.id] : undefined;
        const latestStatus = Array.isArray(statuses) ? statuses[0] : null;
        let statusLabel = "UNKNOWN";
        if (latestStatus && latestStatus.status) {
          statusLabel = latestStatus.status;
        } else if (flow.enabled === false) {
          statusLabel = "PAUSED";
        } else {
          statusLabel = "IDLE";
        }
        const tooltipContent = latestStatus
          ? latestStatus?.message || latestStatus?.errorMessage || `Started at ${formatRunTimestamp(latestStatus.startedAt)}`
          : flow.enabled === false
            ? "Flow is disabled."
            : "This flow has not been executed yet.";
        const statusNode = (
          <Tooltip content={tooltipContent}>
            <Lozenge appearance={getStatusAppearance(statusLabel)} maxWidth={200}>
              {statusLabel}
            </Lozenge>
          </Tooltip>
        );

        return {
          key: flow.id,
          rawName: flow.name,
          name: (
            <Inline align="center">
              <Box>
                <ShareFlowLink flowId={flow.id} className="list-link-icon" />
              </Box>
              <Box>
                |{" "}
                <Link to={`${url}/${flow.id}`} style={{ color: "var(--ds-link, #1677ff);" }} className="atlassian-link">
                  {flow.name}
                </Link>
              </Box>
            </Inline>
          ),
          trigger: getTriggerName(flow?.trigger?.type),
          rawTrigger: flow?.trigger?.type,
          status: statusNode,
          rawStatus: statusLabel,
          modified: formatDate(flow.updated || flow.created, true),
          rawModifiedTimestamp: flow.updated || flow.created || 0,
          enabled: <Toggle defaultChecked={flow.enabled} onChange={() => toggleEnabled(flow)} />,
          rawEnabled: flow.enabled,
          action: <FlowActionMenu flow={flow} />,
          groupName: (flow.groupName || "").trim(),
        };
      }),
    [flowRunnerStatuses, toggleEnabled, tree, url],
  );

  const visibleRows = useMemo(() => {
    const filteredRows = flowRows.filter((row) => {
      const matchesEnabled = !enabledFilter || enabledFilter.includes(row.rawEnabled);
      const matchesTrigger = !triggerFilter || triggerFilter.some((filterValue) => (row.rawTrigger || "").indexOf(filterValue) === 0);

      return matchesEnabled && matchesTrigger;
    });

    if (!sortState?.columnKey || !sortState?.order) {
      return filteredRows;
    }

    const direction = sortState.order === "ascend" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      if (sortState.columnKey === "name") {
        return a.rawName.localeCompare(b.rawName) * direction;
      }

      if (sortState.columnKey === "status") {
        return (a.rawStatus || "").localeCompare(b.rawStatus || "") * direction;
      }

      if (sortState.columnKey === "modified") {
        return (a.rawModifiedTimestamp - b.rawModifiedTimestamp) * direction;
      }

      if (sortState.columnKey === "enabled") {
        return ((a.rawEnabled === true ? 1 : 0) - (b.rawEnabled === true ? 1 : 0)) * direction;
      }

      return 0;
    });
  }, [enabledFilter, flowRows, sortState, triggerFilter]);

  const groupedRows = useMemo(() => {
    const groupedMap = new Map();
    const ungroupedRows = [];

    visibleRows.forEach((row) => {
      if (!row.groupName) {
        ungroupedRows.push(row);
        return;
      }

      if (!groupedMap.has(row.groupName)) {
        groupedMap.set(row.groupName, []);
      }

      groupedMap.get(row.groupName).push(row);
    });

    const groupRows = Array.from(groupedMap.entries())
      .sort(([groupNameA], [groupNameB]) => groupNameA.localeCompare(groupNameB))
      .map(([groupName, rows]) => ({
        key: `group-${groupName}`,
        name: <strong>{groupName}</strong>,
        trigger: "",
        status: "",
        modified: "",
        enabled: "",
        action:
          <Popover
            content={
              <FlowGroupNameEditor
                value={pendingGroupName}
                onChange={setPendingGroupName}
                onPrimaryAction={renameGroup}
                onSecondaryAction={cancelEditGroupName}
                secondaryLabel="Cancel"
              />
            }
            title=""
            trigger="click"
            open={editingGroupName === groupName}
            onOpenChange={(open) => handleEditGroupNameChange(groupName, open)}
          >
            <Button appearance="subtle" iconBefore={EditIcon} onClick={() => startEditGroupName(groupName)}></Button>
          </Popover>,
        isGroupRow: true,
        children: rows,
      }));

    return [...groupRows, ...ungroupedRows];
  }, [cancelEditGroupName, editingGroupName, handleEditGroupNameChange, pendingGroupName, renameGroup, startEditGroupName, visibleRows]);

  const columnsWithFilter = useMemo(
    () =>
      columns.map((column) => {
        const isSortable = column.key === "name" || column.key === "status" || column.key === "modified" || column.key === "enabled";
        const isActiveSortColumn = sortState?.columnKey === column.key ? sortState.order : null;

        if (column.key === "enabled") {
          return {
            ...column,
            filteredValue: enabledFilter ?? null,
            sorter: true,
            sortOrder: isActiveSortColumn,
            onFilter: undefined,
            render: (value, record) => (record?.isGroupRow ? "" : value),
          };
        }

        if (column.key === "trigger") {
          return {
            ...column,
            filteredValue: triggerFilter ?? null,
            onFilter: undefined,
            render: (value, record) => (record?.isGroupRow ? "" : value),
          };
        }

        return {
          ...column,
          sorter: isSortable ? true : column.sorter,
          sortOrder: isActiveSortColumn,
          render: (value, record) => {
            if (record?.isGroupRow && column.key !== "name" && column.key !== "action") {
              return "";
            }

            return value;
          },
        };
      }),
    [enabledFilter, sortState, triggerFilter],
  );

  const tableTheme = useMemo(
    () => ({
      components: {
        Table: {
          headerBg: "var(--ds-background-accent-gray-subtler, #fafafa);",
          headerColor: " var(--ds-text,rgb(4, 4, 4));",
          headerSortActiveBg: "var(--ds-background-accent-gray-subtler-, #fafafa);",
          headerSortHoverBg: "var(--ds-background-accent-gray-subtler-, #fafafa);",
          bodyBg: "var(--ds-surface, #ffffff);",
          rowHoverBg: "var(--ds-background-accent-gray-subtlest, #ffffff);",
          borderColor: "var(--ds-border, #fafafa);",
          colorText: "var(--ds-text, #0A0A0A);",
          colorBgContainer: "var(--ds-surface, #ffffff);",
        },
      },
    }),
    [],
  );

  const tableExpandable = useMemo(
    () => ({
      defaultExpandAllRows: true,
      rowExpandable: (record) => Boolean(record?.children?.length),
      expandIcon: renderExpandIcon,
    }),
    [renderExpandIcon],
  );

  return (
    <>
      <br></br>
      <ConfigProvider theme={tableTheme}>
        <Table
          columns={columnsWithFilter}
          dataSource={groupedRows}
          size="small"
          headerColor=""
          pagination={false}
          onChange={handleTableChange}
          expandable={tableExpandable}
        />
      </ConfigProvider>
      <p>Count: {flows.length}</p>
      {lastLoadedFlowId && (
        <div className="load-more-button-container">
          <Button onClick={loadMore} isDisabled={isLoadingMore} iconBefore={isLoadingMore && <Spinner />}>
            Load more
          </Button>
        </div>
      )}
    </>
  );
};

export default IntegrationListV2;
